<template>
    <div
        id="dashboard-view"
        :class="isDark ? 'dark-bg' : ''"
        class="flex justify-center h-full overflow-auto"
    >
        <div class="dash-grid gap-4 p-8">
            <sidebar-tile
                style="grid-area: left-bar"
                :prof-los="profitAndLossReport"
            />
            <hero-tile style="grid-area: hero"/>
            <tasks-tile style="grid-area: tasks" class="tasks-tile"/>
            <sub-account-age-divided-pie-chart :relation-type="CUSTOMER" style="grid-area: customers" class="customers-tile"/>
            <sub-account-age-divided-pie-chart :relation-type="SUPPLIER" style="grid-area: suppliers" class="suppliers-tile"/>
            <links-tile v-if="false" style="grid-area: links" class="links-tile"/>
            <operations-bars-tile style="grid-area: operations" :profit-and-loss-report="profitAndLossReport"/>
            <bank-balance-tile class="bank-balance-tile" style="grid-area: bank">Numbers 1</bank-balance-tile>
        </div>
    </div>
</template>

<script setup lang="ts">

import {isDark} from '@/theme/theme.service'
import HeroTile from '@/dashboard/tiles/HeroTile.vue'
import BankBalanceTile from '@/dashboard/tiles/BankBalanceTile.vue'
import {onMounted, onUnmounted, ref} from 'vue'
import {fetchProfitAndLossReport} from '@/services/reports/summaryReport.service'
import {formattedDate} from '@/services/formatter.service'
import {useEmitter} from '@/services/infrastructure/emitter'
import {ORG_SWITCHED} from '@/types/events'
import {TRANSACTION_CREATED} from '@/types/transaction/TransactionEvent'
import OperationsBarsTile from '@/dashboard/tiles/OperationsBarsTile.vue'
import SidebarTile from '@/dashboard/tiles/SidebarTile.vue'
import TasksTile from '@/dashboard/tiles/TasksTile.vue'
import LinksTile from '@/dashboard/tiles/LinksTile.vue'
import {ProfLosReport} from '@/proflos/ProfLosReport'
import SubAccountAgeDividedPieChart from '@/dashboard/tiles/SubAccountAgeDividedPieChart.vue'
import {CUSTOMER, SUPPLIER} from '@/subaccount/types/SubAccount'

const {onEvents, cleanUp} = useEmitter()
const profitAndLossReport = ref<ProfLosReport>()

const selectedYear = new Date().getFullYear()

const fetchData = async () => {

    profitAndLossReport.value = await fetchProfitAndLossReport(
        formattedDate(new Date(selectedYear, 0, 1), 'yyyy-MM-dd'),
        formattedDate(new Date(selectedYear, 11, 31), 'yyyy-MM-dd')
    )
}

onMounted(() => {
    fetchData()
    onEvents([ORG_SWITCHED, TRANSACTION_CREATED], fetchData)
})

onUnmounted(cleanUp)


</script>

<style scoped>
@reference "@/index.css";
@reference "tailwindcss";


#dashboard-view {
    background: url("/gradient-light.svg") no-repeat;
    background-size: 100% 100%;

    &.dark-bg {
        background: url("/gradient-dark.svg") no-repeat;
        background-size: 100% 100%;
    }
}

.dash-grid {
    @apply h-full w-full;
    display: grid;
    justify-content: center;
    max-width: 2000px;
    overflow-y: hidden;

    @container (max-width: 1260px) {
        justify-content: start;
    }

    @container (max-width: 700px) {
        overflow-y: auto;
        grid-template-columns: minmax(200px, 100%);
        grid-template-areas:
        "hero"
        "left-bar"
        "customers"
        "suppliers"
        "operations"
        "bank"
        "tasks"
        "links"
    }

    @container (min-width: 700px) and (max-width: 1050px) {
        overflow-y: auto;
        grid-template-columns: minmax(150px, 400px) minmax(220px, 500px);
        grid-template-rows: minmax(420px, 450px)  minmax(180px, 300px) minmax(180px, 300px);
        grid-template-areas:
            "left-bar   hero      "
            "left-bar   customers "
            "left-bar   suppliers"
            "operations operations"
            "tasks     bank     ";
    }

    @container (min-width: 1050px) and (max-width: 1600px) {
        .bank-balance-tile {
            display: none;
        }

        grid-template-columns: minmax(260px, 300px) minmax(420px, 500px) minmax(350px, 500px);
        grid-template-rows: minmax(400px, 420px) minmax(180px, 350px) minmax(220px, 400px);
        grid-template-areas:
            "left-bar   hero        tasks"
            "left-bar   customers   suppliers"
            "left-bar   operations  links";
    }

    @container (min-width: 1600px) {
        grid-template-columns: 300px 500px 500px 300px;
        grid-template-rows: minmax(300px, 400px) minmax(220px, 400px) minmax(220px, 400px);
        grid-template-areas:
            "left-bar   hero        hero        tasks"
            "left-bar   operations  customers   bank"
            "left-bar   links       suppliers   bank";
    }

}


</style>